<template>
  <!-- "white": el CSS global deja transparentes los v-card y los fondos del tab se transparentan (V1 usa lo mismo en su stepper). -->
  <div class="provider-import-v2 white elevation-2 rounded pa-4 ma-3">
    <v-card flat class="pa-4 mb-4">
      <div class="d-flex flex-wrap justify-space-between align-center">
        <div>
          <h2 class="mb-1">Importación de proveedores V2</h2>
          <!-- Con filas cargadas el estado de carga va aquí; sin filas, dentro de la zona de carga. -->
          <div v-if="loading && rows.length" class="busy-line d-flex align-center" role="status">
            <LottiePlayer :animation="loaderAnimation" class="busy-line__art" />
            <span class="text-caption ml-2">{{ busyText }}</span>
          </div>
          <div v-else class="busy-line d-flex align-center text-caption">{{ statusLine }}</div>
        </div>

        <div class="d-flex flex-wrap justify-end">
          <template v-if="importFinished">
            <v-btn small color="primary" class="mr-2 mb-2" @click="closeImport">
              <v-icon left>mdi-close</v-icon>
              Cerrar
            </v-btn>
            <v-btn small color="btn-save" class="mr-2 mb-2" :disabled="loading" @click="clearImport">
              <v-icon left>mdi-file-upload-outline</v-icon>
              Importar otro archivo
            </v-btn>
          </template>
          <v-btn v-else small color="btn-save" class="mr-2 mb-2" :disabled="saveDisabled" @click="confirmImport">
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
          <v-btn small color="btnclean" class="mr-2 mb-2" :disabled="loading || !rows.length" @click="askClear">
            <v-icon left>mdi-broom</v-icon>
            Limpiar
          </v-btn>
          <v-btn small class="mr-2 mb-2 btn-save" :disabled="loading" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Plantilla
          </v-btn>
          <v-btn small color="primary" class="mb-2" :disabled="loading" @click="pickExcel">
            <v-icon left>mdi-file-excel</v-icon>
            Cargar Excel
          </v-btn>
          <input ref="excelInput" type="file" accept=".xlsx,.xls" class="d-none" @change="handleFileChange" />
        </div>
      </div>
    </v-card>

    <ImportSummary v-if="rows.length" :stats="stats" :banners="banners" :bulk-groups="bulkGroupList" @apply-bulk="applyBulkChoice">
      <!-- Verde cuando se puede importar; si quedan filas con errores, también se ofrece verlas solas. -->
      <template #errors>
        <v-btn small text class="mr-2" @click="onlyErrors = !onlyErrors">{{ errorsButtonText }}</v-btn>
        <v-btn color="green darken-3" class="white--text" :disabled="saveDisabled && !loading" :loading="loading" @click="confirmImport">
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </template>
      <template #ready>
        <v-btn color="green darken-3" class="white--text" :disabled="saveDisabled && !loading" :loading="loading" @click="confirmImport">
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </template>
      <!-- Ya no queda nada por guardar: se ofrece cerrar o volver a la pantalla inicial (como Productos V2), en el aviso del resultado o en el de "ya están igual". -->
      <template v-if="importFinished" #[finishedBanner]>
        <v-btn color="primary" class="white--text mr-2" @click="closeImport">
          <v-icon left>mdi-close</v-icon>
          Cerrar
        </v-btn>
        <v-btn color="green darken-3" class="white--text" :disabled="loading" @click="clearImport">
          <v-icon left>mdi-file-upload-outline</v-icon>
          Importar otro archivo
        </v-btn>
      </template>
    </ImportSummary>
    <ImportDropzone
      v-else
      title="Cargar archivo"
      :subtitle="dropzoneSubtitle"
      :how-it-works="howItWorks"
      :tips="tips"
      :loading="loading"
      :busy-text="busyText"
      @template="downloadTemplate"
      @pick="pickExcel"
      @file="readFile"
      @invalid="onInvalidFile"
    />

    <DxDataGrid
      v-if="rows.length"
      ref="grid"
      :class="{ 'is-locked': loading }"
      :data-source="rows"
      :filter-value="gridFilter"
      key-expr="_rowId"
      :show-borders="true"
      :column-auto-width="true"
      :selected-row-keys="selectedRowKeys"
      @selection-changed="onSelectionChanged"
      @editor-preparing="onEditorPreparing"
      @row-updating="onRowUpdating"
      @row-updated="onRowUpdated"
      @cell-prepared="onCellPrepared"
    >
      <DxEditing :allow-updating="isRowEditable" mode="cell" />
      <DxSelection mode="multiple" show-check-boxes-mode="always" />
      <DxPaging :page-size="50" />
      <DxHeaderFilter :visible="true" />

      <DxColumn data-field="status" caption="Estado" :min-width="150" :allow-editing="false" cell-template="status-cell" />
      <DxColumn
        name="detail"
        caption="Detalle"
        cell-template="detail-cell"
        :allow-editing="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
        :width="300"
        css-class="detail-cell"
      />
      <DxColumn data-field="excelRow" caption="Fila" :allow-editing="false" :allow-header-filtering="false" />
      <DxColumn data-field="name" caption="Nombre*" :allow-header-filtering="false" />
      <DxColumn data-field="country" caption="País*" />
      <DxColumn data-field="state" caption="Departamento" />
      <DxColumn data-field="city" caption="Ciudad" :allow-header-filtering="false" />
      <DxColumn data-field="address" caption="Dirección" :allow-header-filtering="false" />
      <DxColumn data-field="phone" caption="Teléfono" :allow-header-filtering="false" />
      <DxColumn data-field="mobile" caption="Celular" :allow-header-filtering="false" />
      <DxColumn data-field="email" caption="Email*" :allow-header-filtering="false" />
      <DxColumn data-field="website" caption="Sitio web" :allow-header-filtering="false" />
      <DxColumn data-field="contactName" caption="Contacto" :allow-header-filtering="false" />
      <DxColumn data-field="contactPhone" caption="Teléfono contacto" :allow-header-filtering="false" />
      <DxColumn data-field="contactMobile" caption="Celular contacto" :allow-header-filtering="false" />
      <DxColumn data-field="contactEmail" caption="Email contacto" :allow-header-filtering="false" />
      <DxColumn data-field="legalName" caption="Razón social" :allow-header-filtering="false" />
      <DxColumn data-field="legalAddress" caption="Dirección legal" :allow-header-filtering="false" />
      <DxColumn data-field="legalEntity" caption="Persona" />
      <DxColumn data-field="nrc" caption="NRC" :allow-header-filtering="false" />
      <DxColumn data-field="nit" caption="NIT" :allow-header-filtering="false" />
      <DxColumn data-field="legalSector" caption="Giro" />
      <DxColumn data-field="taxPayerSize" caption="Categoría" />

      <template #status-cell="{ data }">
        <v-chip x-small dark :color="rowState(data.data).color" :title="rowErrorText(data.data)" v-bind="fixer(data.data, 'status').attrs" v-on="fixer(data.data, 'status').on">
          <v-icon left x-small>{{ rowState(data.data).icon }}</v-icon>
          {{ rowState(data.data).label }}
        </v-chip>
      </template>
      <!-- Plantilla y no calculate-cell-value: el valor calculado queda en caché y no cambia al guardar sin recargar. -->
      <template #detail-cell="{ data }">
        <span v-bind="fixer(data.data, 'detail').attrs" v-on="fixer(data.data, 'detail').on">{{ detailText(data.data) }}</span>
      </template>
    </DxDataGrid>

    <!-- key en cada diálogo: sin key, Vue reutiliza la instancia de otro diálogo y se queda con su slot "cell". -->
    <ImportConfirmDialog
      key="import-dialog"
      v-model="importDialog"
      tone="success"
      icon="mdi-content-save-outline"
      :title="importTitle"
      subtitle="Los nuevos se crean con su ID, para todo el negocio. Los que ya existen se actualizan solo con los campos que traen valor."
      confirm-label="Importar"
      :summary="importSummary"
      :columns="importColumns"
      :rows="importRows"
      :total="selectedValidRows.length"
      :notice="importNotice"
      @confirm="runImport"
    >
      <template #cell="{ row, column }">
        <strong v-if="column.key === 'name'">{{ row.name }}</strong>
        <template v-else>{{ row[column.key] }}</template>
      </template>
    </ImportConfirmDialog>

    <ImportConfirmDialog
      key="leave-dialog"
      v-model="leaveDialog"
      tone="warning"
      :icon="leaveIcon"
      :title="leaveTitle"
      :subtitle="leaveSubtitle"
      :confirm-label="leaveConfirmLabel"
      :summary="leaveSummary"
      @confirm="runLeave"
      @input="onLeaveInput"
    ></ImportConfirmDialog>

    <ImportRowDialog
      v-model="rowDialog" :title="`Corregir fila ${editingRow ? editingRow.excelRow : ''}`" :subtitle="editingRow ? editingRow.name || 'Sin nombre' : ''" :row="editingRow"
      :fields="rowFields" :groups="rowGroups" :items-for="itemsForField" :validate="validateDraft" :has-next="rowDialog && !!nextErrorRow(editingRow)"
      @apply="applyRow($event)" @apply-next="applyRow($event, true)"
    />

    <alerts v-if="alert.show" :properties="alert" @close_alert="closeAlert" @accept_alert="acceptAlert"></alerts>
  </div>
</template>

<script>
import saveAs from "file-saver";
import { DxDataGrid, DxColumn, DxEditing, DxPaging, DxSelection, DxHeaderFilter } from "devextreme-vue/data-grid";
import Alerts from "@/components/Alerts";
import LottiePlayer from "@/components/LottiePlayer";
import ImportConfirmDialog from "@/components/inventory/ImportConfirmDialog";
import ImportDropzone from "@/components/inventory/ImportDropzone";
import ImportRowDialog from "@/components/inventory/ImportRowDialog";
import ImportSummary from "@/components/inventory/ImportSummary";
import loaderAnimation from "@/assets/lottie/loader.json";
import { PREVIEW_ROWS, cleanText, formatCount, plural, readExcelRows, buildTemplate, chunk, batchImportKey, splitServerErrors, summarizeServerErrors, diffFields, forgetServer, applyChanges, previewRow } from "@/helpers/importV2";
import {
  BATCH_SIZE, TEMPLATE_ROWS, COLUMNS, TEXT_KEYS, buildCatalogs, parseProviderRows, evaluateRows, editorItems, cascadeLocation, bulkGroups, applyBulk,
  toPayload, templateLists, instructionRows, rowErrorText, rowDetail, canSend, isOpen, statusBanner, resultBanner,
} from "@/helpers/providerImportV2";

const MAX_FILE_BYTES = 5 * 1024 * 1024; // el único tope del archivo: filas sin límite, se guardan por lotes
const SHEET_NAME = "Proveedores"; // hoja de la plantilla: la misma que se lee al cargar (si no existe, se lee la primera)
// Columna Estado. Tonos con contraste ≥ 4.5:1 frente a texto blanco en chips x-small.
const STATES = {
  imported: { label: "Importada", icon: "mdi-check-circle", color: "green darken-3" },
  updated: { label: "Actualizada", icon: "mdi-check-circle-outline", color: "teal darken-3" },
  willUpdate: { label: "Se actualizará", icon: "mdi-database-edit-outline", color: "indigo darken-1" },
  unchanged: { label: "Sin cambios", icon: "mdi-database-check-outline", color: "blue-grey darken-2" },
  ready: { label: "Lista", icon: "mdi-check", color: "blue darken-2" },
  unselected: { label: "Sin seleccionar", icon: "mdi-minus-circle-outline", color: "grey darken-2" },
  errors: { label: "Con errores", icon: "mdi-alert-circle", color: "error" },
};
// Diálogo de trabajo pendiente: textos según lo que se confirma (leaveAction).
const LEAVE = {
  close: { title: "¿Salir sin importar?", icon: "mdi-exit-run", confirm: "Salir y descartar" },
  replace: { title: "¿Reemplazar el archivo cargado?", icon: "mdi-file-replace-outline", confirm: "Reemplazar y elegir archivo" },
  clear: { title: "¿Limpiar la importación?", icon: "mdi-broom", confirm: "Limpiar y descartar" },
};
// Columnas del diálogo de confirmación.
const IMPORT_COLUMNS = [
  { key: "excelRow", label: "Fila" },
  { key: "name", label: "Proveedor" },
  { key: "legalName", label: "Razón social" },
  { key: "nit", label: "NIT" },
  { key: "country", label: "País" },
];
// Pantalla vacía: el flujo real en 4 pasos y las reglas de la plantilla (hoja Instrucciones).
const HOW_IT_WORKS = [
  "Descargue la plantilla y llene la hoja Proveedores. Obligatorios: nombre, país, email y teléfono o celular; en El Salvador, también persona, giro, NIT, departamento y ciudad.",
  "Cargue el archivo: arrástrelo a la zona punteada o búsquelo en su equipo. No hay límite de filas.",
  "Revise la tabla. Las celdas en rojo tienen errores y se corrigen aquí mismo, sin volver al Excel.",
  `Presione Guardar. Se envían lotes de ${BATCH_SIZE} proveedores, uno tras otro: los nuevos se crean y los que ya existen se actualizan.`,
];
const TIPS = [
  "Si el proveedor ya existe (mismo NIT, nombre o razón social) se actualiza solo con lo que traiga lleno: una celda vacía no borra el dato guardado.",
  "Fuera de El Salvador bastan nombre, país, email y teléfono o celular. En El Salvador además persona, giro, NIT o DUI (9 o 14 dígitos), departamento y ciudad, y NRC (1 a 8 dígitos) si la persona es jurídica.",
  `En El Salvador solo PERSONA NATURAL o PERSONA JURIDICA: una S.A. o Corporación se registra como PERSONA JURIDICA.`,
  "País, departamento, ciudad, persona, giro y categoría: use los nombres de la hoja Catalogos de la plantilla.",
  "Los proveedores nuevos quedan disponibles en todas las sucursales del negocio.",
];
const ROW_GROUPS = [ // formulario de corrección de una fila: las columnas en 3 bloques
  { title: "Proveedor", keys: ["name", "legalName", "legalEntity", "nit", "nrc", "legalSector", "taxPayerSize"] },
  { title: "Ubicación", keys: ["country", "state", "city", "address", "legalAddress"] },
  { title: "Contacto", keys: ["email", "phone", "mobile", "website", "contactName", "contactPhone", "contactMobile", "contactEmail"] },
];
const STATUS_LINE = `Obligatorios: nombre, país, email y teléfono o celular (en El Salvador, también persona, giro, NIT, departamento y ciudad). Los que ya existen se actualizan. Sin límite de filas: se guarda en lotes de ${BATCH_SIZE}.`;

export default {
  name: "ManageProviderImportV2",
  components: { DxDataGrid, DxColumn, DxEditing, DxPaging, DxSelection, DxHeaderFilter, Alerts, LottiePlayer, ImportConfirmDialog, ImportDropzone, ImportRowDialog, ImportSummary },
  data() {
    return {
      loading: false,
      saving: false, // solo los POST de importar (loading también cubre leer el archivo): mientras duran, la ventana no se puede cerrar
      busyText: "",
      importDialog: false,
      leaveDialog: false,
      leaveAction: "close", // qué confirma el diálogo: "close" (salir de la ventana) | "replace" (cargar otro Excel) | "clear" (Limpiar)
      onlyErrors: false, // la tabla muestra solo las filas con errores
      importColumns: IMPORT_COLUMNS,
      dropzoneSubtitle: `Excel .xlsx o .xls de hasta ${MAX_FILE_BYTES / 1048576} MB, sin límite de filas: se guarda en lotes de ${BATCH_SIZE} proveedores.`,
      statusLine: STATUS_LINE,
      howItWorks: HOW_IT_WORKS,
      tips: TIPS,
      catalogs: null, // índices congelados (buildCatalogs)
      rows: [], // Limitación conocida: sin tope de filas, Vue observa cada una (~12 KB por fila: 20 000 filas ≈ 250 MB); el único tope es el de 5 MB del archivo.
      selectedRowKeys: [],
      rowDialog: false, editingRow: null, // formulario de corrección de una fila: abierto, y la fila que se corrige
      result: null, // { created, updated, unchanged } acumulado de los lotes guardados de este archivo
      alert: { type: "success", show: false, header: "", body: "", options: null },
    };
  },
  computed: {
    selectedValidRows() {
      const selected = new Set(this.selectedRowKeys);
      return this.rows.filter((row) => selected.has(row._rowId) && canSend(row));
    },
    counts() {
      const count = (status) => this.rows.filter((row) => row.status === status).length;
      const ready = this.selectedValidRows;
      return {
        errors: count("Con errores"), news: count("Lista"), updates: count("Se actualizará"), unchanged: count("Sin cambios"),
        ready: ready.length, readyUpdates: ready.filter((row) => row.status === "Se actualizará").length, open: this.rows.filter(isOpen).length,
      };
    },
    stats() {
      const { errors, news, updates, unchanged, ready } = this.counts;
      return [
        { key: "loaded", label: "Cargadas", value: formatCount(this.rows.length), tone: "info", icon: "sheet" },
        { key: "errors", label: "Con errores", value: formatCount(errors), tone: errors ? "danger" : "muted", icon: "alert" },
        { key: "news", label: "Nuevas", value: formatCount(news), tone: news ? "info" : "muted", icon: "sheet" },
        { key: "updates", label: "Se actualizarán", value: formatCount(updates), tone: updates ? "info" : "muted", icon: "sheet" },
        ...(unchanged ? [{ key: "unchanged", label: "Sin cambios", value: formatCount(unchanged), tone: "muted", icon: "sheet" }] : []),
        { key: "ready", label: "Seleccionadas y válidas", value: formatCount(ready), tone: ready ? "success" : "muted", icon: "check" },
      ];
    },
    banners() {
      return [this.result && resultBanner(this.result), statusBanner(this.counts, !!this.result)].filter(Boolean);
    },
    // Errores de catálogo que se repiten en 2 o más filas: una sola corrección para todas.
    bulkGroupList() {
      return this.counts.errors ? bulkGroups(this.rows, this.catalogs) : [];
    },
    errorsButtonText() {
      const errors = this.counts.errors;
      if (this.onlyErrors) return "Ver todas";
      return errors === 1 ? "Ver solo la fila con errores" : `Ver solo las ${formatCount(errors)} filas con errores`;
    },
    gridFilter() {
      return this.onlyErrors && this.counts.errors ? ["status", "=", "Con errores"] : null;
    },
    // Hay filas sin guardar que no son "Sin cambios": salir las perdería.
    hasUnsavedWork() {
      return this.rows.some(isOpen);
    },
    // Aviso que lleva los botones de cierre cuando ya no queda nada por guardar: el del resultado (tras guardar) o el de "ya están igual".
    finishedBanner() {
      return this.result ? "result" : "done";
    },
    // Todo lo cargado ya se guardó o ya estaba igual: en vez de Guardar se ofrece cerrar o cargar otro archivo.
    importFinished() {
      return this.rows.length > 0 && !this.hasUnsavedWork;
    },
    saveBlockReason() {
      if (this.loading) return "Procesando...";
      if (!this.selectedValidRows.length) return this.rows.length ? "No hay filas seleccionadas y válidas." : "Cargue un archivo.";
      return "";
    },
    saveDisabled() {
      return !!this.saveBlockReason;
    },
    // Diálogo de confirmación al guardar. Las listas solo se calculan con el diálogo abierto (el archivo puede traer miles de filas).
    importTitle() {
      return `Importar ${plural(this.counts.ready, "proveedor", "proveedores")}`;
    },
    importSummary() {
      if (!this.importDialog) return [];
      const { ready, readyUpdates } = this.counts;
      return [
        { icon: "mdi-plus-circle-outline", text: plural(ready - readyUpdates, "nuevo", "nuevos") },
        { icon: "mdi-pencil-circle-outline", text: plural(readyUpdates, "se actualiza", "se actualizan") },
        { icon: "mdi-layers-outline", text: `Se guardan en ${plural(Math.ceil(ready / BATCH_SIZE), "lote", "lotes")} de hasta ${BATCH_SIZE}` },
        { icon: "mdi-domain", text: "Los nuevos, disponibles en todas las sucursales" },
      ];
    },
    importRows() {
      if (!this.importDialog) return [];
      return this.selectedValidRows.slice(0, PREVIEW_ROWS).map(({ excelRow, name, legalName, nit, country }) => ({ excelRow, name, legalName, nit, country }));
    },
    // Filas pendientes que este guardado no incluye, con el motivo.
    importNotice() {
      if (!this.importDialog) return "";
      const included = new Set(this.selectedValidRows.map((row) => row._rowId));
      const left = this.rows.filter((row) => !row.saved && !included.has(row._rowId));
      if (!left.length) return "";
      const reasons = [
        [left.filter((row) => row.status === "Con errores").length, "con errores"],
        [left.filter((row) => row.status === "Sin cambios").length, "sin cambios"],
        [left.filter(canSend).length, "sin seleccionar"],
      ].filter(([count]) => count).map(([count, reason]) => `${formatCount(count)} ${reason}`);
      return `${plural(left.length, "fila no se importará", "filas no se importarán")} ahora: ${reasons.join(" · ")}.`;
    },
    leaveTitle() {
      return LEAVE[this.leaveAction].title;
    },
    leaveIcon() {
      return LEAVE[this.leaveAction].icon;
    },
    leaveConfirmLabel() {
      return LEAVE[this.leaveAction].confirm;
    },
    leaveSubtitle() {
      const left = this.rows.filter(isOpen).length;
      if (!left) return "Se reemplazará el archivo cargado por el que elija. Lo ya importado no se deshace.";
      return `${plural(left, "proveedor sin importar se perderá", "proveedores sin importar se perderán")}. Los cambios que hizo en la tabla no se guardan.`;
    },
    leaveSummary() {
      const saved = this.rows.filter((row) => row.saved).length;
      return saved ? [{ icon: "mdi-check-circle-outline", text: `${plural(saved, "proveedor ya importado", "proveedores ya importados")}: no se deshace` }] : [];
    },
  },
  created() {
    // JSON de Lottie y listas de catálogo sin reactividad: Vue no necesita observarlos.
    this.loaderAnimation = loaderAnimation;
    this.sources = null; // { countries, departments, municipalities, activities }: con ellos se reconstruyen los índices al refrescar proveedores
    this.touchedFields = null;
    this.rowFields = COLUMNS; this.rowGroups = ROW_GROUPS; // campos y bloques del formulario de corrección: sin reactividad
    // Clave de cada lote por huella de su contenido { huella -> clave }; no reactivo. Un lote idéntico (reintento) reenvía la misma clave (ver saveBatch).
    // Limitación conocida: vive solo en este componente; recargar la página o salir de la pantalla pierde las claves (mejora posible: sessionStorage).
    this.importKeys = new Map();
  },
  mounted() {
    this.loadCatalogs();
    // Cerrar o recargar el navegador con filas sin importar: el navegador pide confirmación.
    this.onBeforeUnload = (event) => {
      if (!this.hasUnsavedWork) return;
      event.preventDefault();
      event.returnValue = "";
    };
    if (typeof window !== "undefined") window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    if (typeof window !== "undefined") window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    // ---------- Catálogos ----------
    async loadCatalogs() {
      this.loading = true;
      this.busyText = "Cargando catálogos…";
      try {
        const [providers] = await Promise.all([this.$API.providers.getProviders(), this.$store.dispatch("getCatalogMH")]);
        let countries = this.$store.getters.getCountries;
        if (!Array.isArray(countries) || !countries.length) {
          await this.$store.dispatch("getCountries");
          countries = this.$store.getters.getCountries;
        }
        const catalog = (name) => this.$store.getters.getCatalogMH(name) || [];
        this.sources = { countries: Array.isArray(countries) ? countries : [], departments: catalog("Departamento"), municipalities: catalog("Municipio"), activities: catalog("ActividadEconomica") };
        this.catalogs = buildCatalogs({ ...this.sources, providers: providers || [] });
      } catch (error) {
        this.showAlert("danger", "Error", `No se pudieron cargar los catálogos: ${this.errorMessage(error)}`);
      } finally {
        this.loading = false;
      }
    },
    // Si la carga inicial falló, se reintenta al descargar la plantilla o cargar un Excel.
    async ensureCatalogs() {
      if (!this.catalogs) await this.loadCatalogs();
      return !!this.catalogs;
    },
    // La lista de proveedores se vuelve a pedir antes de comparar contra lo guardado: otro usuario pudo crear o cambiar proveedores desde que se abrió la pantalla.
    async refreshProviders() {
      try {
        this.catalogs = buildCatalogs({ ...this.sources, providers: (await this.$API.providers.getProviders()) || [] });
      } catch (error) {
        throw new Error(`No se pudo actualizar la lista de proveedores: ${this.errorMessage(error)}`);
      }
    },

    // ---------- Plantilla y carga ----------
    async downloadTemplate() {
      if (this.loading || !(await this.ensureCatalogs())) return;
      try {
        const blob = await buildTemplate({
          sheetName: SHEET_NAME,
          columns: COLUMNS,
          textKeys: TEXT_KEYS,
          lists: templateLists(this.catalogs),
          instructions: instructionRows(this.catalogs),
          validationRows: TEMPLATE_ROWS,
        });
        saveAs(blob, "Plantilla_Importacion_Proveedores_V2.xlsx");
      } catch (error) {
        this.showAlert("danger", "Error", `No se pudo generar la plantilla: ${this.errorMessage(error)}`);
      }
    },
    // "Cargar Excel": con un archivo ya cargado se pregunta ANTES de abrir el selector; solo si confirma se abre y se reemplaza.
    pickExcel() {
      if (this.loading) return;
      if (this.rows.length) {
        this.leaveAction = "replace";
        this.leaveDialog = true;
        return;
      }
      this.$refs.excelInput.click();
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      event.target.value = "";
      return file ? this.readFile(file) : undefined;
    },
    onInvalidFile(message) {
      this.showAlert("warning", "Archivo no válido", message);
    },
    async readFile(file) {
      if (this.loading || !(await this.ensureCatalogs())) return;
      this.loading = true;
      this.busyText = file.size > 2 * 1024 * 1024 ? "Leyendo archivo grande… puede tardar unos segundos" : "Leyendo archivo…";
      try {
        const sheetRows = await readExcelRows(file, { maxBytes: MAX_FILE_BYTES, sheetName: SHEET_NAME });
        await this.refreshProviders();
        const rows = parseProviderRows(sheetRows, this.catalogs);
        if (!rows.length) {
          this.showAlert("warning", "Sin proveedores", "No se encontraron filas con datos en la hoja Proveedores.");
          return;
        }
        evaluateRows(rows, this.catalogs);
        this.rows = rows;
        this.selectedRowKeys = rows.map((row) => row._rowId);
        this.result = null;
        this.onlyErrors = false;
        const count = (status) => formatCount(rows.filter((row) => row.status === status).length);
        const summary = `${plural(rows.length, "fila cargada", "filas cargadas")}: ${count("Lista")} nuevas, ${count("Se actualizará")} por actualizar, ${count("Sin cambios")} sin cambios y ${count("Con errores")} con errores.`;
        // Más de un lote: aviso (no bloquea) de que se guarda por partes y de que una falla a mitad deja lo anterior guardado. Sin número de lotes:
        // depende de cuántas filas se envían (las "Sin cambios" y con errores no); el diálogo de Guardar dice el número exacto.
        if (rows.length <= BATCH_SIZE) this.showAlert("success", "Archivo cargado", summary);
        else this.showAlert("warning", "Archivo cargado", `${summary} El archivo trae ${plural(rows.length, "proveedor", "proveedores")}: al guardar se envían en lotes de ${BATCH_SIZE}, uno tras otro. Cada lote se guarda por separado; si uno falla, los anteriores quedan guardados.`);
      } catch (error) {
        this.showAlert("danger", "Error", this.errorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    // ---------- Tabla ----------
    isRowEditable(e) {
      return !e.row.data.saved;
    },
    onSelectionChanged(e) {
      this.selectedRowKeys = e.selectedRowKeys;
    },
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;
      if (e.command === "select") {
        if (e.row && !canSend(e.row.data)) e.editorOptions.disabled = true;
        return;
      }
      const items = e.row && e.dataField && this.catalogs ? editorItems(e.row.data, e.dataField, this.catalogs) : null;
      if (!items) return; // texto libre: queda el editor de texto
      // Desplegable por fila en columnas sin lookup: DevExtreme 23.1 deja cambiar editorName y editorOptions aquí, fila por fila.
      const options = {
        ...e.editorOptions,
        items,
        searchEnabled: true,
        showClearButton: true,
        dropDownOptions: { minWidth: 320 },
        onValueChanged: (args) => {
          e.setValue(args.value);
          // En modo celda lo elegido queda pendiente hasta salir de la celda: si lo eligió el usuario se cierra la edición para que se guarde al instante.
          if (args.event) setTimeout(() => e.component.closeEditCell(), 0);
        },
      };
      delete options.valueChangeEvent; // es del editor de texto: en el desplegable está obsoleta y DevExtreme avisa en la consola
      if (e.dataField === "city" && !items.length) options.noDataText = "Elija primero el departamento"; // sin departamento válido la lista sale vacía
      e.editorName = "dxSelectBox";
      e.editorOptions = options;
    },
    // Solo campos planos en newData: DevExtreme hace merge profundo y no borraría llaves de objetos anidados.
    onRowUpdating(e) {
      Object.keys(e.newData).forEach((field) => { e.newData[field] = cleanText(e.newData[field]); }); // null (vaciar el desplegable) = ""
      Object.assign(e.newData, cascadeLocation(e.oldData, e.newData, this.catalogs));
      this.touchedFields = { key: e.key, fields: Object.keys(e.newData) };
    },
    // Lo editado deja de depender de los errores del servidor (y de un "sin cambios" que dijo el servidor).
    onRowUpdated(e) {
      const row = this.rows.find((item) => item._rowId === e.key);
      const fields = this.touchedFields && this.touchedFields.key === e.key ? this.touchedFields.fields : [];
      if (row) forgetServer(row, fields);
      this.touchedFields = null;
      this.revalidate();
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || !e.column.dataField || !e.data.errors) return;
      const message = e.data.errors[e.column.dataField];
      if (message) {
        e.cellElement.classList.add("cell-error");
        e.cellElement.title = message;
      }
    },
    revalidate(reload) {
      evaluateRows(this.rows, this.catalogs);
      this.repaintGrid(reload);
    },
    // repaint() redibuja con los valores en caché de la tabla: tras cambiar filas sin pasar por ella (corrección en bloque) o con el filtro "solo errores" hay que consultar de nuevo.
    repaintGrid(reload) {
      const grid = this.$refs.grid && this.$refs.grid.instance;
      if (!grid) return;
      if (reload || this.gridFilter) grid.refresh();
      else grid.repaint();
    },
    // Lo que ve el usuario en la columna Estado: además del estado de la fila cuenta si está marcada para importar.
    rowState(row) {
      const state = { Importada: STATES.imported, Actualizada: STATES.updated, "Sin cambios": STATES.unchanged, "Con errores": STATES.errors }[row.status];
      if (state) return state;
      if (!this.selectedRowKeys.includes(row._rowId)) return STATES.unselected;
      return row.status === "Se actualizará" ? STATES.willUpdate : STATES.ready;
    },
    rowErrorText,
    detailText(row) {
      return this.rowState(row) === STATES.unselected ? "Sin seleccionar: no se importará." : rowDetail(row);
    },
    applyBulkChoice({ group, value }) {
      applyBulk(group, value);
      this.revalidate(true);
    },
    // Chip de Estado (filas con errores) y texto de Detalle (filas con texto) abren el formulario de corrección; las filas guardadas no se editan.
    fixer(row, cell) {
      if (row.saved || !(cell === "status" ? row.status === "Con errores" : this.detailText(row))) return { attrs: {}, on: {} };
      const open = (event) => { event.preventDefault(); if (!this.loading) { this.editingRow = row; this.rowDialog = true; } }; // ocupado (leyendo o guardando): la tabla está bloqueada
      return { attrs: { class: "row-opener", role: "button", tabindex: "0", "aria-label": `Corregir la fila ${row.excelRow}` }, on: { click: open, keydown: (event) => { if (event.key === "Enter" || event.key === " ") open(event); } } };
    },
    itemsForField(field, draft) {
      return this.catalogs ? editorItems(draft, field.key, this.catalogs) : null;
    },
    // Vista previa del formulario: la misma evaluación de la tabla, sobre copias de las filas (las reales no cambian hasta Aplicar).
    validateDraft(draft) {
      const copy = previewRow(this.rows, this.editingRow, diffFields(this.editingRow, draft), (rows) => evaluateRows(rows, this.catalogs));
      return { errors: copy.errors, status: copy.status, detail: rowDetail(copy) };
    },
    // Aplicar: lo cambiado se escribe como al editar la celda (cascada de ciudad, sin lo que dijo el servidor) y la tabla se refresca; "y siguiente" abre la próxima fila con errores.
    applyRow(changes, next = false) {
      const row = this.editingRow;
      if (applyChanges(row, { ...changes, ...cascadeLocation(row, changes, this.catalogs) })) this.revalidate(true);
      this.editingRow = (next && this.nextErrorRow(row)) || row;
      this.rowDialog = this.editingRow !== row;
    },
    nextErrorRow(row) {
      return this.rows.slice(this.rows.indexOf(row) + 1).find((item) => item.status === "Con errores");
    },

    // ---------- Guardar ----------
    confirmImport() {
      if (!this.saveDisabled) this.importDialog = true;
    },
    runImport() {
      this.importDialog = false;
      this.saveImport();
    },
    // Guarda por lotes de BATCH_SIZE, uno tras otro, esperando cada respuesta. Si un lote falla se detiene: los anteriores quedan guardados
    // y guardar otra vez manda solo lo que falta (lo guardado sale de la selección).
    // Limitación conocida: si se cierra la pestaña o se corta la red a mitad, los lotes ya enviados quedan guardados y el resto no; al
    // cargar el archivo otra vez, lo ya guardado sale como "Sin cambios".
    async saveImport() {
      if (this.saveBlockReason) return;
      const batches = chunk(this.selectedValidRows, BATCH_SIZE);
      let saved = 0; // lotes ya guardados en esta pasada
      this.loading = true;
      this.saving = true;
      try {
        while (saved < batches.length) {
          this.busyText = `Guardando lote ${saved + 1} de ${batches.length}…`;
          await this.saveBatch(batches[saved]);
          saved++;
        }
      } catch (error) {
        this.showBatchError(error, batches, saved);
      } finally {
        this.saving = false; // ya no hay POST vivo: salir no lo deja colgado (va primero: si algo de lo que sigue fallara, la X no quedaría bloqueada)
        if (saved) await this.refreshAfterSave();
        this.revalidate();
        this.loading = false;
      }
    },
    // Un lote: su POST con su clave y la respuesta aplicada a sus filas. Los índices de la respuesta son posiciones en el lote, no en la tabla.
    async saveBatch(batch) {
      const providers = batch.map((row) => toPayload(row, this.catalogs));
      const { key, fingerprint } = batchImportKey(this.importKeys, providers);
      const response = await this.$API.providers.importProvidersV2({ importKey: key, providers });
      this.importKeys.delete(fingerprint); // guardado: el mismo contenido más adelante es otra importación
      const data = (response && response.data) || {};
      const [created, updated, unchanged] = ["created", "updated", "unchanged"].map((name) => (data[name] || []).filter(({ index }) => batch[index]));
      created.forEach(({ index, ID_Provider }) => Object.assign(batch[index], { saved: "created", ID_Provider }));
      updated.forEach(({ index, ID_Provider, fields }) => Object.assign(batch[index], { saved: "updated", ID_Provider, changes: Array.isArray(fields) ? fields : [] }));
      unchanged.forEach(({ index, ID_Provider }) => { batch[index].existing = { ID_Provider, by: "servidor" }; }); // ya estaba igual: no se vuelve a enviar
      const total = this.result || { created: 0, updated: 0, unchanged: 0 };
      this.result = { created: total.created + created.length, updated: total.updated + updated.length, unchanged: total.unchanged + unchanged.length };
      const done = new Set([...created, ...updated, ...unchanged].map(({ index }) => batch[index]._rowId));
      this.selectedRowKeys = this.selectedRowKeys.filter((rowKey) => !done.has(rowKey));
    },
    // El lote que falló: los errores del servidor van a la celda de la fila de ESE lote; la alerta dice qué quedó guardado y qué falló.
    showBatchError(error, batches, saved) {
      const errors = (error && error.errors) || [];
      Object.entries(splitServerErrors(errors, "providers").rows).forEach(([index, fields]) => {
        const row = batches[saved][index];
        if (row) row.serverErrors = { ...row.serverErrors, ...fields };
      });
      let body = errors.length ? summarizeServerErrors(errors, "providers") : this.errorMessage(error);
      if (batches.length > 1) {
        const rows = batches.slice(0, saved).reduce((sum, batch) => sum + batch.length, 0);
        const progress = saved ? `Se guardaron ${saved} de ${batches.length} lotes (${plural(rows, "proveedor", "proveedores")}).` : "No se guardó ningún lote.";
        body = `${progress} Falló el lote ${saved + 1}: ${/[.!?]$/.test(body) ? body : `${body}.`}${errors.length ? "" : " Guarde otra vez: se envía solo lo que falta."}`;
      }
      this.showAlert(errors.length ? "warning" : "danger", errors.length ? "Revise las celdas en rojo" : "Error", body);
    },
    // Lista de proveedores al día (para que las filas sin guardar se comparen contra lo recién creado o actualizado) y lista de la pantalla de proveedores.
    async refreshAfterSave() {
      try {
        await this.refreshProviders();
        this.$store.dispatch("reloadData", "providers");
      } catch (error) {
        // Sin esto el guardado igual terminó bien: la lista se actualiza al cargar el próximo Excel.
      }
    },

    // ---------- Salir con trabajo pendiente ----------
    askLeave() {
      if (this.saving) {
        // Salir y descartar dejaría los POST vivos: el servidor guardaría igual los proveedores y nadie vería el resultado. Se pide esperar.
        // Limitación conocida: si la X se pulsa desde otra pestaña, la alerta queda oculta con esta pestaña (el cierre sí se bloquea).
        this.showAlert("warning", "Guardando proveedores", "Se están guardando los proveedores; espere a que termine.");
        this.$emit("leave-cancel"); // el cierre quedó abortado: AddProduct olvida lo que ya confirmaron los demás importadores
        return;
      }
      this.leaveAction = "close";
      this.leaveDialog = true;
    },
    // Limpiar con filas sin importar pide confirmación (un clic sin querer perdería el trabajo); sin pendientes limpia directo.
    askClear() {
      if (!this.hasUnsavedWork) return this.clearImport();
      this.leaveAction = "clear";
      this.leaveDialog = true;
    },
    runLeave() {
      this.leaveDialog = false;
      if (this.leaveAction === "clear") return this.clearImport();
      if (this.leaveAction === "replace") {
        // Sigue dentro del clic de confirmar: el navegador deja abrir el selector de archivos.
        if (this.$refs.excelInput) this.$refs.excelInput.click();
        return;
      }
      this.$emit("close");
    },
    // Cancelaron la pregunta de salir (Cancelar, Esc o clic fuera; confirmar no pasa por aquí): AddProduct olvida lo que ya confirmaron los demás importadores.
    onLeaveInput(open) {
      if (!open && this.leaveAction === "close") this.$emit("leave-cancel");
    },
    // Cerrar la ventana de "Agregar nuevo producto" (la X de arriba); la pantalla que contiene a V2 hace el cierre.
    closeImport() {
      this.$emit("close");
    },
    clearImport() {
      this.rows = [];
      this.selectedRowKeys = [];
      this.result = null;
      this.onlyErrors = false;
    },

    // ---------- Alertas ----------
    errorMessage(error) {
      if (!error) return "Error desconocido";
      if (typeof error === "string") return error;
      return error.message || "Error desconocido";
    },
    showAlert(type, header, body, options = null) {
      this.alert = { type, show: true, header, body, options };
    },
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
  },
};
</script>

<style scoped>
.busy-line {
  min-height: 28px;
}

.busy-line__art {
  width: 28px;
  height: 28px;
}

.is-locked {
  pointer-events: none;
}

/* V1 agranda este ícono en toda la app (".mdi-file-excel::before { font-size: 32px !important }" sin scope): aquí vuelve al tamaño de los demás. */
.provider-import-v2 ::v-deep .v-btn .mdi-file-excel::before {
  font-size: 18px !important;
}

/* Regla global "table i { color: #3498db !important }": sin esto los íconos de los chips de la tabla salen azules. */
.provider-import-v2 ::v-deep .dx-datagrid .v-chip .v-icon {
  color: #fff !important;
}

.provider-import-v2 ::v-deep .cell-error {
  background-color: #fdecea !important;
  box-shadow: inset 0 0 0 1px #e53935;
}

/* Texto largo a la izquierda: estilos sin scoped de veterinaria centran todas las celdas con !important. */
.provider-import-v2 ::v-deep .dx-datagrid-content td.detail-cell {
  text-align: left !important;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.35;
}
</style>
